import { isDfCRequest } from '../../../../../web/utils/statelessUtil';
import {
  IconNames,
  RequestConstants,
} from '../../../../models/constants/constants';
import {
  createSelector,
  IAppState,
  IModalitySectionState,
  UITabView,
  UITabViewInfo,
  UITabViewMode,
} from '../../../../models/reduxState';
import { getEmailFromCustomerContacts, shallowEquals, deepCopyProperty } from '../../../../utilities';
import { currentUITabViewInfoSelector, currentViewModeSelector, isAadOrUnauthSelector, isMsaOrUnauthSelector, isBATenantSelector, isNonAgaveSelector, isRequestDetailsSelector, isSignedInSelector, modalitySectionStateSelector, showModalitiesStickyFooterBRSSelector } from '../../../app/selectors/contactUsSelector';
import { Region, RegionType } from '../../models/modalityModels';
import {
  FreelanceRequest,
  Note,
  RequestModality,
  RequestSeverity,
  RequestState,
  RequestStatusStep,
  RequestStepsSchema,
  RequestUIState,
} from '../../models/requestModels';
import { dirtyRequestSelector, areNotesValidSelector, isAttachmentsUpdatedSelector, isContactsUpdatedSelector } from './requestDetailsSelectors';
import { pristineRequestSelector } from './requestHistorySelectors';
import { regionInfoSelector } from './sharedStateSelectors';
import { getString } from '../../../../../web/utils/stringLoader';

export function getUserDescription(request: FreelanceRequest): string {
  if (request) {
    return request.UserDescription;
  }
  return String();
}

export const getRequestTitle = (request: FreelanceRequest): string => {
  if (!!request) {
    const requestTitle = request.Title;
    if (!!requestTitle) {
      return requestTitle;
    }
    // If there is no title value from the request, the title will be set to the user description
    const userDescription = request.UserDescription;
    if (!!userDescription) {
      request.Title = userDescription.slice(
        0,
        Math.min(
          userDescription.length,
          RequestConstants.TitleTextFieldCharLimit
        )
      );
    }
  }
  return request.Title;
};

export function getParatureTicketNumber(request: FreelanceRequest): string {
  if (request) {
    return request.ParatureTicketNumber;
  }
  return String();
}

export function getUserPhone(request: FreelanceRequest): string {
  if (request) {
    return request.UserPhone;
  }
  return String();
}

export const getCreateDateTime = (request: FreelanceRequest): Date | null => {
  if (!!request) {
    const dateTime = request.CreateDateTime;
    return !!dateTime ? new Date(dateTime) : null;
  }

  return null;
};

export const getAcknowledgeDateTime = (
  request: FreelanceRequest
): Date | null => {
  if (!!request) {
    const dateTime = request.AcknowledgeDateTime;
    return !!dateTime ? new Date(dateTime) : null;
  }

  return null;
};

export const getClosedDateTime = (request: FreelanceRequest): Date | null => {
  if (!!request) {
    const dateTime = request.ServiceClosedDateTime;
    return !!dateTime ? new Date(dateTime) : null;
  }

  return null;
};

export const getMSSupportTicket = (request: FreelanceRequest): string => {
  if (!!request && !!request.MSSupportTicket) {
    return request.MSSupportTicket;
  }

  return String();
};

export const getIsClosed = (request: FreelanceRequest): boolean => {
  if (request) {
    const requestState = getState(request);
    switch (requestState) {
      case RequestState.Closed:
      case RequestState.Cancelled:
      case RequestState.Completed:
        // should we use getIsFeedbackClosed(request) ?
        return true;
    }
  }

  return false;
};

export const draftRequestSelector = createSelector(
  modalitySectionStateSelector,
  (m: IModalitySectionState) => m.draftRequest
);

export const getRequest = (
  currentUiTabView: UITabViewInfo,
  draftRequest: FreelanceRequest,
  pristineRequest: FreelanceRequest,
  dirtyRequest: FreelanceRequest,
  showModalitiesStickyFooterBRS: boolean
): FreelanceRequest => {
  if (!!currentUiTabView) {
    // we currently don't have request history or request edit for stickyFooter == false, so return draft request
    if (!showModalitiesStickyFooterBRS) {
      return draftRequest;
    }
    switch (currentUiTabView.uITabView) {
      case UITabView.requestCreation:
        return draftRequest;
      case UITabView.requestDetails:
        return currentUiTabView.viewMode === UITabViewMode.edit ? dirtyRequest : pristineRequest;
      default:
        return pristineRequest;
    }
  }
  return null;
};

export const requestSelector = createSelector(
  [currentUITabViewInfoSelector, draftRequestSelector, pristineRequestSelector, dirtyRequestSelector, showModalitiesStickyFooterBRSSelector],
  getRequest
);

export const isRequestClosedSelector = createSelector(
  requestSelector,
  getIsClosed
);

export const getModality = (request: FreelanceRequest): RequestModality => {
  if (!!request) {
    return request.Modality;
  }

  return RequestModality.Undefined;
};

export const getModalityIconName = (request: FreelanceRequest): string => {
  switch (getModality(request)) {
    case RequestModality.Chat:
      return IconNames.ChatIconName;
    case RequestModality.Phone:
      return IconNames.ContactIconName;
    case RequestModality.Email:
      return IconNames.MailIconName;
    case RequestModality.IVR:
      return IconNames.PhoneIcon;
    case RequestModality.C2C:
      return IconNames.PhoneIcon;
    default:
      return IconNames.ContactIconName;
  }
};

export const getState = (request: FreelanceRequest): RequestState => {
  let requestState: RequestState | undefined;
  if (!!request) {
    requestState = RequestState[request.State as keyof typeof RequestState];
  }

  return requestState === undefined ? RequestState.Unknown : requestState;
};

export const getUIState = (request: FreelanceRequest): RequestUIState => {
  const state = getState(request);
  switch (state) {
    case RequestState.Active:
    case RequestState.Delayed:
      return RequestUIState.Opened;
    case RequestState.Acknowledged:
      return RequestUIState.AgentAssigned;
    case RequestState.Closed:
    case RequestState.Completed:
      return RequestUIState.Closed;
    case RequestState.Cancelled:
      return RequestUIState.Cancelled;
    default:
      return RequestUIState.Opened;
  }
};

export const isRequestHistoryEnabledSelector = (state: IAppState): boolean => {
  const isBATenant = isBATenantSelector(state);
  const isAadOrUnauth = isAadOrUnauthSelector(state);
  const isMsaOrUnauth = isMsaOrUnauthSelector(state);
  return (state.ContactState.ModalitySectionState.isOrgId && state.ContactState.RequestHistoryState.isSearchRequestsEnabledCommercial && !isBATenant && isAadOrUnauth) ||
    (isBATenant && state.ContactState.RequestHistoryState.isSearchRequestsEnabledBA) ||
    (!state.ContactState.ModalitySectionState.isOrgId && state.ContactState.RequestHistoryState.isSearchRequestsEnabledConsumer && isMsaOrUnauth);
};

export const isRequestHistoryTabEnabledSelector = (state: IAppState): boolean => {
  const isBATenant = isBATenantSelector(state);
  const isAadOrUnauth = isAadOrUnauthSelector(state);
  const isMsaOrUnauth = isMsaOrUnauthSelector(state);
  return (state.ContactState.ModalitySectionState.isOrgId && state.ContactState.RequestHistoryState.isSearchRequestsEnabledCommercial && !isBATenant && isAadOrUnauth) ||
    (!state.ContactState.ModalitySectionState.isOrgId && state.ContactState.RequestHistoryState.isSearchRequestsEnabledConsumer && isMsaOrUnauth) ||
    (isBATenant && state.ContactState.RequestHistoryState.isSearchRequestsEnabledBA) ||
    isNonAgaveSelector(state);
};

export const isSupportNavEnabledSelector = (state: IAppState) => {
  const showHistoryTab = isRequestHistoryTabEnabledSelector(state);
  const isNonAgave = isNonAgaveSelector(state);
  /**
   * We're checking if showHistoryTab == true to show support nav until designer provides feedback
   */
  return showHistoryTab && (state.ContactState.SharedState.showSupportNav || isNonAgave);
};

export const isUseIconsForSupportNavEnabledSelector = (state: IAppState) => {
  const isNonAgave = isNonAgaveSelector(state);
  return state.ContactState.SharedState.useIconsForSupportNav || isNonAgave;
};

export const isAttachmentsEnabledSelector = (state: IAppState): boolean => {
  const isBATenant = isBATenantSelector(state);
  return (
    state.ContactState.ModalitySectionState.isSignedIn &&
    ((state.ContactState.ModalitySectionState.isOrgId &&
      state.ContactState.AttachmentsState.isAttachmentsEnabledCommercial) ||
      (!state.ContactState.ModalitySectionState.isOrgId &&
        state.ContactState.AttachmentsState.isAttachmentsEnabledConsumer &&
        !state.ContactState.ModalitySectionState.isDfC) ||
        (isBATenant && state.ContactState.AttachmentsState.isAttachmentsEnabledBA))
  );
};

const getUserEmail = (request: FreelanceRequest, currentUiTabView: UITabViewInfo) => {
  if (!!request) {
    // for request editing we haven't yet updated customerContacts while the user types so return userEmail which has the ';' separated emails,
    // rave takes the email from the first customerContacts and sets it on UserEmail during UpdateContacts.
    // In request details, we'll take the emails from customerContacts and concatenate them with ';'
    if (currentUiTabView && currentUiTabView.uITabView === UITabView.requestDetails && currentUiTabView.viewMode === UITabViewMode.edit) {
      return request.UserEmail;
    } else {
      return getEmailFromCustomerContacts(request);
    }
  }
  return '';
};

export const userEmailSelector = createSelector(
  [requestSelector, currentUITabViewInfoSelector],
  getUserEmail
);

export const requestTitleSelector = createSelector(
  [requestSelector],
  getRequestTitle
);

export const userDescriptionSelector = createSelector(
  [requestSelector],
  getUserDescription
);

export const modalitySelector = createSelector(
  requestSelector,
  getModality
);

export const getStatusSteps = (
  request: FreelanceRequest
): RequestStepsSchema[] => {
  const requestStatusStrings = {
    requestChatStepTitle: getString('requestStatus_requestChatStepTitle'),
    requestChatStepContent: getString('requestStatus_requestChatStepContent'),
    requestChatStepSubContent: getString('requestStatus_requestChatStepSubContent'),
    requestOpenedStepTitle: getString('requestStatus_requestOpenedStepTitle'),
    requestOpenedStepContent: getString('requestStatus_requestOpenedStepContent'),
    requestAcknowledgedStepTitle: getString('requestStatus_requestAcknowledgedStepTitle'),
    requestAcknowledgedStepContent: getString('requestStatus_requestAcknowledgedStepContent'),
    requestResolvedStepTitle: getString('requestStatus_requestResolvedStepTitle'),
    scheduledCallbackScheduledText: getString('requestStatus_scheduledCallbackScheduledText'),
    agentPendingStep: getString('requestStatus_agentPendingStep'),
    issueResolutionPendingStep: getString('requestStatus_issueResolutionPendingStep')
  };

  if (!request) {
    return [];
  }
  let customerScheduledCallbackMessage = String();
  if (
    !!request!.CustomerFeedbackDateTime &&
    shallowEquals(request!.Modality, RequestModality.Phone)
  ) {
    customerScheduledCallbackMessage =
      requestStatusStrings.scheduledCallbackScheduledText
        .replace(
          '{0}',
          new Date(request!.CustomerFeedbackDateTime!).toLocaleDateString()
        )
        .replace(
          '{1}',
          new Date(request!.CustomerFeedbackDateTime!).toLocaleTimeString([], {
            hour: RequestConstants.DateTimeFormat as any,
            minute: RequestConstants.DateTimeFormat as any,
          })
        );
  }

  const RequestSteps = [] as RequestStepsSchema[];
  RequestSteps.push(
    {
      Id: RequestStatusStep.Opened,
      Title: shallowEquals(request!.Modality, RequestModality.Chat)
        ? requestStatusStrings.requestChatStepTitle
        : requestStatusStrings.requestOpenedStepTitle,
      ContentText: shallowEquals(request!.Modality, RequestModality.Chat)
        ? requestStatusStrings.requestChatStepContent
        : requestStatusStrings.requestOpenedStepContent +
          customerScheduledCallbackMessage,
      ContentSubText: shallowEquals(request!.Modality, RequestModality.Chat)
        ? requestStatusStrings.requestChatStepSubContent
        : String(),
      Date: request!.CreateDateTime
        ? new Date(request!.CreateDateTime!).toLocaleString()
        : null,
      Active:
        Object.is(RequestState[request!.State], RequestState.Active) ||
        Object.is(RequestState[request!.State], RequestState.Acknowledged) ||
        Object.is(RequestState[request!.State], RequestState.Delayed) ||
        Object.is(RequestState[request!.State], RequestState.Closed) ||
        Object.is(RequestState[request!.State], RequestState.Completed) ||
        Object.is(RequestState[request!.State], RequestState.Cancelled),
      PhoneNumber: request!.PhoneNumber || '',
      LinkText: String(),
      IsStepInProcess:
        Object.is(RequestState[request!.State], RequestState.Active) ||
        Object.is(RequestState[request!.State], RequestState.Delayed),
    },
    {
      Id: RequestStatusStep.Acknowledged,
      Title: requestStatusStrings.requestAcknowledgedStepTitle,
      ContentText: shallowEquals(request!.Modality, RequestModality.Chat)
        ? requestStatusStrings.requestAcknowledgedStepContent
        : requestStatusStrings.requestAcknowledgedStepContent +
          customerScheduledCallbackMessage,
      ContentSubText: shallowEquals(request!.Modality, RequestModality.Chat)
        ? requestStatusStrings.requestChatStepSubContent
        : String(),
      Date: request!.AcknowledgeDateTime
        ? new Date(request!.AcknowledgeDateTime!).toLocaleString()
        : null,
      Active:
        Object.is(RequestState[request!.State], RequestState.Acknowledged) ||
        Object.is(RequestState[request!.State], RequestState.Closed) ||
        Object.is(RequestState[request!.State], RequestState.Completed) ||
        Object.is(RequestState[request!.State], RequestState.Cancelled),
      PhoneNumber: request!.PhoneNumber || '',
      LinkText: String(),
      IsStepInProcess: Object.is(
        RequestState[request!.State],
        RequestState.Acknowledged
      ),
    }
  );

  RequestSteps.push({
    Id: RequestStatusStep.Resolved,
    Title: requestStatusStrings.requestResolvedStepTitle,
    ContentText: String(),
    ContentSubText: String(),
    Date: request!.ClosedDateTime
      ? new Date(request!.ClosedDateTime!).toLocaleString()
      : null,
    Active:
      Object.is(RequestState[request!.State], RequestState.Closed) ||
      Object.is(RequestState[request!.State], RequestState.Completed) ||
      Object.is(RequestState[request!.State], RequestState.Cancelled),
    PhoneNumber: request!.PhoneNumber || '',
    LinkText: String(),
    IsStepInProcess:
      Object.is(RequestState[request!.State], RequestState.Closed) ||
      Object.is(RequestState[request!.State], RequestState.Completed) ||
      Object.is(RequestState[request!.State], RequestState.Cancelled),
  });

  return RequestSteps;
};

export const statusStepsSelector = createSelector(
  [
    (_state: IAppState, props: any) => props.request
  ],
  getStatusSteps
);

export const getIsRequestSaveBtnEnabled = (
  isPhoneNumberValid: boolean,
  isEmailValid: boolean,
  userContactsUpdated: boolean,
  userAttachmentsUpdated: boolean,
  userNotesValid: boolean,
  severity: RequestSeverity,
  businessImpactConfirmed: boolean,
  customerAvailabilityConfirmed: boolean
): boolean => {
  return (
    !(
      // No updates
      (!userContactsUpdated && !userAttachmentsUpdated && !userNotesValid) ||
      // contacts are updated but either phone or email is not valid
      (userContactsUpdated && (!isPhoneNumberValid || !isEmailValid)) ||
      // Severiy is Critical, Sev A or Sev1 but businessImpact or customerAvailability is not confirmed
      ((severity === RequestSeverity.Critical ||
           severity === RequestSeverity.Sev1 ||
           severity === RequestSeverity.SevA) &&
           (!businessImpactConfirmed || !customerAvailabilityConfirmed))
    )
  );
};

export const isRequestEditedSelector = createSelector(
  [isContactsUpdatedSelector, isAttachmentsUpdatedSelector, areNotesValidSelector],
  (userContactsUpdated: boolean, userAttachmentsUpdated: boolean, userNotesValid: boolean) => {
    // If one of the three updated, users can proceed to save the edited request details.
    return !!userContactsUpdated|| !!userAttachmentsUpdated|| !!userNotesValid;
  }
);

const getUserCountryDialingCode = (request: FreelanceRequest): string => {
  return !!request && !!request.UserCountryDialingCode ? request.UserCountryDialingCode : '';
};

export const userCountryDialingCodeSelector = createSelector(
  requestSelector,
  getUserCountryDialingCode
);

const getUserPhoneWithoutDialingCode = (request: FreelanceRequest): string => {
  return !!request && !!request.UserPhoneWithoutDialingCode ? request.UserPhoneWithoutDialingCode : '';
};

export const userPhoneWithoutDialingCodeSelector = createSelector(
  requestSelector,
  getUserPhoneWithoutDialingCode
);

export const userPhoneSelector = createSelector(
  requestSelector,
  (r: FreelanceRequest) => r ? r.UserPhone : ''
);

const getUserCountryDialingCodeIndex = (
  request: FreelanceRequest
): string => {
  let index;
  if (!!request) {
    index = request.UserPhoneDialingCodeIndex;
  }
  return index;
};

export const userPhoneDialingCodeIndexSelector = createSelector(
  requestSelector,
  getUserCountryDialingCodeIndex
);

export const twoDigitCountryCodeSelector = createSelector(
  requestSelector,
  (r: FreelanceRequest) => (!!r ? r.TwoDigitCountryCode : '')
);

const getNotes = (
  request: FreelanceRequest
): Note[] => {
  return !!request ? deepCopyProperty(request.Notes) : [];
};

export const notesSelector = createSelector(
  [requestSelector],
  getNotes
);

const getPlaceHolder = (
  regionInfo: Array<Region>,
  userCountryDialingCode: string,
  twoDigitCountryCode: string
): { region: Region; index: number } => {
  const regexPattern = /[^0-9]/g;
  let region: Region = null;
  let index = -1;
  regionInfo.find((r: Region, idx: number) => {
    if (r.DialingCode) {
      const d = r.DialingCode.replace(regexPattern, '');
      // for requests that were created before we added twoDigitCountryCode to extensionAttributes, we'll use dialingCode
      // problem with using dialingCode is that there might be multiple regions that have the same dialingCode,
      // so the first match might not be the country the user selected when creating the request
      if (!!twoDigitCountryCode) {
        if (r.TwoDigitCountryCode === twoDigitCountryCode) {
          region = r;
          index = idx;
          return true;
        }
      } else if (!!userCountryDialingCode) {
        if (d === userCountryDialingCode) {
          region = r;
          index = idx;
          return true;
        }
      }
    }
    return false;
  });
  // if dialing code was not found, fetch placeHolder
  if (index === -1) {
    regionInfo.find((r: Region, idx: number) => {
      if (r.RegionType === RegionType.PlaceHolder) {
        region = r;
        index = idx;
        return true;
      }
      return false;
    });
  }
  return { region, index };
};

export const phoneFieldPlaceHolderSelector = createSelector(
  [
    regionInfoSelector,
    userCountryDialingCodeSelector,
    twoDigitCountryCodeSelector,
  ],
  getPlaceHolder
);

export const getSeverity = (request: FreelanceRequest): RequestSeverity => {
  if (!!request) {
    return request.Severity || RequestSeverity.None;
  }
  return RequestSeverity.None;
};

export const severitySelector = createSelector(
  [requestSelector],
  getSeverity
);

export const isDFCRequestSelector = createSelector([requestSelector], (request: FreelanceRequest): boolean => {
  if (request && request.RequestId) {
    return isDfCRequest(request.RequestId);
  }
  return false;
});

export const isRequestDetailsEditModeSelector = createSelector(
  [isSignedInSelector, isRequestDetailsSelector, currentViewModeSelector, isDFCRequestSelector],
  (
    isSignedIn: boolean,
    isRequestDetails: boolean,
    currentViewMode: UITabViewMode,
    isDFCRequest: boolean
  ) => isSignedIn && isRequestDetails && currentViewMode === UITabViewMode.edit && !isDFCRequest
);
